import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { LienReleaseTemplate } from '../../../domains/invoices/lien-release-template/lien-release-template.model';
import { WorkOrderForm, WorkOrderTagForm } from '../draft-work-order.form';

/**
 * TT-260 item 4. The VTI threshold is chosen by rank, which is how people
 * talk about installers, instead of the old 0 to 5 number. Each rank's floor
 * is the lowest VTI that earns it, using the same bands the backend scores with
 * (VtiRatingService.getRank), so "Expert" means "Expert or higher".
 */
export const VTI_RANK_FLOORS = [
  { name: 'Rookie', floor: 0 },
  { name: 'Intermediate', floor: 0.25 },
  { name: 'Experienced', floor: 0.75 },
  { name: 'Proficient', floor: 1.5 },
  { name: 'Expert', floor: 2.5 },
  { name: 'Master', floor: 3.5 },
  { name: 'Artisan', floor: 4.5 },
  { name: 'Elite Artisan', floor: 5.25 },
];

/** The rank a threshold falls in: the last one whose floor it reaches. */
export const rankIndexFor = (threshold?: number | null): number => {
  const value = Number(threshold ?? 0);
  let index = 0;
  VTI_RANK_FLOORS.forEach((rank, i) => {
    if (value >= rank.floor) index = i;
  });
  return index;
};

@Component({
  selector: 'gc-publishing-step',
  templateUrl: './publishing-step.component.html',
  styleUrls: ['./publishing-step.component.scss'],
})
export class PublishingStepComponent implements OnInit, OnDestroy {
  @Input() form!: WorkOrderForm;
  @Input() lienReleaseTemplates!: LienReleaseTemplate[];

  tagForm = new WorkOrderTagForm();
  readonly ranks = VTI_RANK_FLOORS;
  readonly rankIndex = new FormControl<number>(0, { nonNullable: true });
  private rankChanges?: Subscription;

  ngOnInit(): void {
    this.rankIndex.setValue(rankIndexFor(this.form.minimumHammerRatingThreshold.value), { emitEvent: false });
    this.rankChanges = this.rankIndex.valueChanges.subscribe(index => {
      const rank = VTI_RANK_FLOORS[index] ?? VTI_RANK_FLOORS[0];
      this.form.minimumHammerRatingThreshold.setValue(rank.floor);
      this.form.minimumHammerRatingThreshold.markAsDirty();
    });
  }

  ngOnDestroy(): void {
    this.rankChanges?.unsubscribe();
  }

  addTag() {
    if (this.tagForm.invalid) {
      return;
    }

    this.form.addTag(this.tagForm.updatedModel);
    this.tagForm.reset();
  }

  removeTag(index: number) {
    this.form.removeTagAt(index);
  }

  /** The pin on the slider reads the rank, not a number. */
  public rankName(index: number) {
    return VTI_RANK_FLOORS[index]?.name ?? '';
  }
}
